import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  styled,
} from '@mui/material';
import { 
  Visibility as VisibilityIcon,
  CompareArrows as CompareArrowsIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { PageGradient } from '../components/PageGradient';
import GetListedGraphic from '../assets/images/get-listed-graphic.png';
import { TypeformStyle, Question } from '../components/TypeformStyle';
import { createContactLeadService } from '../services/contactLeadService';
import { useRepositories } from '../repositories/RepositoryProvider';

const BenefitCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  padding: '12px 32px',
  fontSize: '18px',
  borderRadius: '30px',
  background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  textTransform: 'none',
  fontWeight: 600,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
    opacity: 0.5,
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.primary.main})`,
    '&::before': {
      opacity: 0.7,
    }
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
  },
  '&.MuiButton-containedSecondary': {
    background: 'white',
    color: theme.palette.primary.main,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
    '&:hover': {
      background: 'white',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.12)',
    },
    '&::before': {
      background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 100%)',
    }
  }
}));

export const GetListedPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const ctaSectionRef = useRef<HTMLDivElement>(null);
  const repository = useRepositories();
  const contactLeadService = createContactLeadService(repository.contactLeadRepository);

  const handleOpenForm = () => {
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  const handleScrollToCTA = () => {
    ctaSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const benefits = [
    {
      icon: <VisibilityIcon fontSize="large" color="primary" />,
      title: 'Increased Visibility',
      description: 'Get your shipping services in front of thousands of potential customers looking for reliable shipping solutions.',
    },
    {
      icon: <CompareArrowsIcon fontSize="large" color="primary" />,
      title: 'Fair Comparison',
      description: 'Showcase your competitive rates and superior service quality alongside other providers.',
    },
    {
      icon: <TrendingUpIcon fontSize="large" color="primary" />,
      title: 'Business Growth',
      description: 'Connect with customers actively searching for shipping services between the UK and Africa.',
    },
  ];

  const formQuestions: Question[] = [
    {
      id: 'name',
      question: "What's your business name?",
      placeholder: 'Type your business name',
      validation: (value: string) => value.length >= 2 ? '' : 'Please enter a valid business name',
    },
    {
      id: 'email',
      question: 'What\'s your email address?',
      placeholder: 'name@company.com',
      validation: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Please enter a valid email',
    },
    {
      id: 'phone',
      question: 'What\'s your phone number?',
      placeholder: '+44',
      validation: (value: string) => /^\+?[1-9]\d{1,14}$/.test(value) ? '' : 'Please enter a valid phone number',
    },
    {
      id: 'message',
      question: 'Tell us about your shipping services',
      placeholder: 'Type your message here...',
      validation: (value: string) => value.length >= 10 ? '' : 'Please enter at least 10 characters',
      multiline: true,
      rows: 4,
    },
  ];

  /**
   * Handles the submission of the form
   * @param formData - The form data from the typeform
   */
  const handleFormSubmit = async (formData: Record<string, string>) => {
    await contactLeadService.submitLead({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
    });
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <PageGradient />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box 
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            alignItems: 'center',
            gap: 3,
            py: { xs: 4, md: 6 },
          }}
        >
          <Box 
            sx={{
              flex: 1,
              textAlign: { xs: 'center', md: 'left' },
              maxWidth: { md: '60%' }
            }}
          >
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              fontWeight="bold"
              sx={{
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                lineHeight: 1.2,
              }}
            >
              Get Your Shipping Business Listed
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              paragraph
              sx={{
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              }}
            >
              Join the leading comparison platform for UK to Africa shipping services
            </Typography>
            <CTAButton
              variant="contained"
              color="primary"
              size="large"
              onClick={handleScrollToCTA}
              sx={{ mt: 2 }}
            >
              Get Listed Now
            </CTAButton>
          </Box>
          <Box 
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              maxWidth: { xs: '100%', md: '40%' },
              mx: 'auto',
              mb: { xs: 2, md: 0 },
            }}
          >
            <Box
              component="img"
              src={GetListedGraphic}
              alt="Get Listed Illustration"
              sx={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: { xs: '220px', sm: '280px', md: '320px' },
                objectFit: 'contain',
                borderRadius: 2,
              }}
            />
          </Box>
        </Box>

        {/* Benefits Section */}
        <Grid container spacing={4} py={4}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} md={4} key={index}>
              <BenefitCard>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box mb={2}>{benefit.icon}</Box>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    {benefit.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </BenefitCard>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action Section */}
        <Box
          ref={ctaSectionRef}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            p: 6,
            mt: 6,
            mb: 4,
            textAlign: 'center',
            minHeight: '600px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            scrollMarginTop: '2rem',
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            Ready to Grow Your Business?
          </Typography>
          <Typography paragraph>
            Join our platform and reach customers actively looking for shipping services.
          </Typography>
          <TypeformStyle
            questions={formQuestions}
            onSubmit={handleFormSubmit}
            successMessage={{
              title: 'Thank you for your interest!',
              description: 'We\'ll review your application and get back to you within 2 working days.',
            }}
            submitButtonText="Submit Application"
            continueButtonText="Next"
          />
        </Box>

      </Container>
    </Box>
  );
}; 